import { useContext } from 'react';
import { MediaGalleryContext } from '../../../contexts/pdp/media-gallery.context';
import { type Media } from '../../../helpers/images/images.helper';

export const DEFAULT_CARD_WIDTH = 94;

// hook to update all the media gallery components when a new product in the gallery is selected
export const useUpdateMediaProduct = (items: Media[]) => {
	const { activeIndex, thumbnailSlides, translateThumbnailPercentage, thumbnailMaxScrollWidth, dispatch } =
		useContext(MediaGalleryContext);

	const updateSlideIndex = (incrementBy: number) => {
		const newPrevSlide = thumbnailSlides.prevSlide + incrementBy;
		const newNextSlide = thumbnailSlides.nextSlide + incrementBy;

		dispatch({
			type: 'SET_THUMBNAIL_SLIDES',
			payload: {
				prevSlide: newPrevSlide,
				nextSlide: newNextSlide
			}
		});
	};

	const handleNavigationThroughCarousel = (selectedIndex: number, widthOfContentCardPixels: number, slidesPerView: number) => {
		const isIncrementing = selectedIndex - activeIndex >= 1;
		const isDecrementing = selectedIndex - activeIndex <= -1;
		const { prevSlide, nextSlide } = thumbnailSlides;

		let translatePercentage = translateThumbnailPercentage;

		const handleIncrement = () => {
			const offsetValue = slidesPerView > 1 ? 2 : 1;
			if (slidesPerView >= 5) {
				if (selectedIndex > nextSlide) {
					updateSlideIndex(selectedIndex - nextSlide);
					translatePercentage = (selectedIndex - (slidesPerView - offsetValue)) * widthOfContentCardPixels;
				}
			} else {
				translatePercentage = (selectedIndex - (slidesPerView - offsetValue)) * widthOfContentCardPixels;
			}
		};

		const handleDecrement = () => {
			const offsetValue = slidesPerView > 1 ? 1 : 0;
			if (slidesPerView >= 5) {
				if (selectedIndex < prevSlide) {
					updateSlideIndex(selectedIndex - prevSlide);
					translatePercentage = (selectedIndex - offsetValue) * widthOfContentCardPixels;
				}
			} else {
				translatePercentage = (selectedIndex - offsetValue) * widthOfContentCardPixels;
			}
		};

		if (isIncrementing) {
			handleIncrement();
		} else if (isDecrementing) {
			handleDecrement();
		}

		// keeps the translatePercentage within the bounds of the carousel
		translatePercentage = Math.min(Math.max(translatePercentage, thumbnailMaxScrollWidth), 0);

		dispatch({ type: 'SET_TRANSLATE_THUMBNAIL_PERCENTAGE', payload: translatePercentage });
	};

	const updateCarouselSlides = (selectedIndex: number, widthOfContentCardPixels?: number, slidesPerView?: number): void => {
		dispatch({ type: 'SET_ACTIVE_INDEX', payload: selectedIndex });
		dispatch({ type: 'SET_TRANSLATE_GALLERY_PERCENTAGE', payload: selectedIndex * -100 });
		dispatch({ type: 'SET_THUMBNAIL_TRANSITION_SPEED', payload: 0.3 });

		if (widthOfContentCardPixels === undefined || slidesPerView === undefined || items.length < slidesPerView) return;

		handleNavigationThroughCarousel(selectedIndex, -widthOfContentCardPixels, slidesPerView);
	};

	return { updateCarouselSlides, handleNavigationThroughCarousel };
};
